import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  canonical?: string;
  type?: string;
  schema?: object;
}

const SEO = ({ 
  title, 
  description = "Shop Much More connects diaspora communities across the UK with the authentic products they love from home. Discover traditional foods, spices, and essentials.",
  image = "/favicon.png", // Branded fallback image
  canonical,
  type = "website",
  schema
}: SEOProps) => {
  const siteName = "ShopMuchMore";
  const fullTitle = title ? `${title} | ${siteName}` : siteName;
  
  // Use environment variable if available, otherwise fallback to window.location.origin
  const baseUrl = (import.meta.env.VITE_FRONTEND_BASE_URL as string | undefined)?.replace(/\/$/, "") || 
                  (typeof window !== 'undefined' ? window.location.origin : 'https://shopmuchmore.co.uk');
  
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const url = canonical || currentUrl;

  // Ensure image URL is absolute for social media crawlers
  const absoluteImage = image 
    ? (image.startsWith('http') ? image : `${baseUrl}${image.startsWith('/') ? '' : '/'}${image}`)
    : `${baseUrl}/favicon.png`;

  return (
    <Helmet>
      {/* Standard metadata tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type === 'product' ? 'og:product' : type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:secure_url" content={absoluteImage} />
      <meta property="og:image:alt" content={title || siteName} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* X (formerly Twitter) */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImage} />

      {/* Other important tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="author" content="ShopMuchMore" />

      {/* Structured Data */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
